// react
import { useContext, useMemo, useState } from 'react';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// types
import {
	Entity,
	Frame,
	Ifr,
	Loan,
	Pagination,
	Transaction,
	TransactionAugmented,
} from '../../../../@types';
import { transactionStatuses } from '../../../../@types/unions';

// router
import { useLocation, useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardTransaction } from '../../../cards/CardTransaction/CardTransaction';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// utils
import { translateStatus } from '../../../../utils/translations';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableCashBenefitsProps {
	frame?: Frame;
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TableCashBenefits = ({
	frame = 'PROJECT',
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TableCashBenefitsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramCategory = searchParams.get('category');
	const paramComponent = searchParams.get('component');
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('expenseType');
	const paramLoan = searchParams.get('loan');
	const paramStatus = searchParams.get('status');
	const paramTags = searchParams.get('tags');
	const paramToEntity = searchParams.get('toEntity');

	// defaults
	const defaultFiltersValues = {
		category: 'all',
		component: 'all',
		expenseType: 'all',
		loan: 'all',
		status: 'all',
		tags: [],
		toEntity: 'all',
	};

	// state
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		category: paramCategory || defaultFiltersValues.category,
		component: paramComponent || defaultFiltersValues.component,
		expenseType: paramExpenseType || defaultFiltersValues.expenseType,
		loan: paramLoan || defaultFiltersValues.loan,
		status: paramStatus || defaultFiltersValues.status,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
		toEntity: paramToEntity || defaultFiltersValues.toEntity,
	});
	const [ifrs, setIfrs] = useState<Ifr[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);

	// fetch beneficiaries
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?fields=id,name&project=${project?.id}&type=BENEFICIARY`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}
		},
	});

	// fetch loans
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/loans?fields=id,code&project=${project?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setLoans(res.data);
		},
	});

	// fetch ifrs
	useFetch({
		isEnabled: Boolean(project?.id && featureFlags['IFRS']),
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&status=!DELETED&fields=currency,dateEnd,dateStart,id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setIfrs(res.data);
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: frame === 'BENEFICIARY' ? 2 : 1.5,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: frame === 'BENEFICIARY' ? 2 : 1.5,
			},
			label: t('classification'),
			value: 'expenseType',
		},
	];
	if (frame !== 'ENTITY') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('entity'),
			value: 'fromEntity',
		});
	}

	if (frame !== 'BENEFICIARY') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('beneficiary'),
			value: 'toEntity',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: 1,
			},
			label: t('traceability'),
			value: 'traceability',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('account'),
			value: 'account',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'modified',
		}
	);

	const idFormFilters = 'form-cash-benefits-filters';

	// options
	const optionsBeneficiaries = beneficiaries.map((beneficiary) => {
		return {
			label: beneficiary.name,
			value: String(beneficiary.id),
		};
	});

	const optionsStatus = transactionStatuses.map((status) => {
		return {
			label: translateStatus(status, t),
			value: status,
		};
	});

	const optionsLoans = loans.map((loan) => {
		return {
			label: loan.code,
			value: String(loan.id),
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	if (frame === 'PROJECT') {
		if (userProjectPermissions?.beneficiary?.read) {
			filters.push({
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-beneficiary`,
				label: t('beneficiary'),
				name: 'toEntity',
				onChange: handleChangeFilterValue,
				options: optionsBeneficiaries,
				type: 'SELECT',
				value: filtersValues.toEntity,
			});
		}
	}

	if (userProjectPermissions?.component?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-component`,
			label: t('component'),
			name: 'component',
			onChange: handleChangeFilterValue,
			type: 'COMPONENT',
			value: filtersValues.component,
		});
	}
	if (userProjectPermissions?.category?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-category`,
			label: t('category'),
			name: 'category',
			onChange: handleChangeFilterValue,
			type: 'CATEGORY',
			value: filtersValues.category,
		});
	}

	if (userProjectPermissions?.expenseType?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-expense-type`,
			label: t('expenseType'),
			name: 'expenseType',
			onChange: handleChangeFilterValue,
			type: 'EXPENSE_TYPE',
			value: filtersValues.expenseType,
		});
	}

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		onChange: handleChangeFilterValue,
		options: optionsStatus,
		type: 'SELECT',
		value: filtersValues.status,
	});

	if (userProjectPermissions?.loan?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-loan`,
			label: t('financingSource'),
			name: 'loan',
			onChange: handleChangeFilterValue,
			options: optionsLoans,
			type: 'SELECT',
			value: filtersValues.loan,
		});
	}

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.cashBenefits.content'),
			},
			IconCircleProps: {
				color: 'grey',
				type: 'cashBenefit',
			},
			title: { children: t('empty.cashBenefits.title') },
		};

		if (
			emptyProps.actions &&
			userProjectPermissions?.cashBenefit.create &&
			!location.pathname.includes('entity') &&
			project?.version !== '2'
		) {
			emptyProps.actions.push({
				children: t('recordCashBenefit'),
				onClick: () => {
					searchParams.set('dialog', 'recordCashBenefit');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		}

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [
		location,
		paramDialog,
		project?.version,
		searchParams,
		setSearchParams,
		t,
		userProjectPermissions,
	]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					// add ifrs ids to transaction
					const transactionAugmented: TransactionAugmented = {
						...transaction,
						ifrs: ifrs.filter(
							(ifr) => ifr.dateStart <= transaction.date && ifr.dateEnd >= transaction.date
						),
					};

					return (
						<Grid item key={`payment-${i}`} xs={12}>
							<CardTransaction
								frame={frame}
								refetchTransactions={refetchTransactions}
								transaction={transactionAugmented}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, ifrs, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="cashBenefits" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={4} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnExport resource="CASH_BENEFIT" />
					</Grid>
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
